import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GUIDES } from "../data/guides";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";

const guidesJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Bathroom Remodel Cost Guides",
  url: `${SITE_URL}/guides`,
  description:
    "A library of cost guides for U.S. bathroom remodels — by scope, room size, and region. Real numbers, no fluff.",
  hasPart: GUIDES.map((g) => ({
    "@type": "Article",
    headline: g.title,
    url: `${SITE_URL}/guides/${g.slug}`,
    description: g.subtitle,
  })),
};

export default function GuidesIndex() {
  return (
    <div data-testid="guides-index-page" className="container-x py-16 lg:py-24">
      <SEO
        title="Bathroom Remodel Cost Guides — by scope, size & region"
        description="Real bathroom remodel cost research for U.S. homeowners. 10+ guides covering small baths, master baths, shower remodels, and regional cost differences."
        path="/guides"
        jsonLd={guidesJsonLd}
      />
      <div className="max-w-3xl mb-14">
        <p className="overline mb-4">Cost guides</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter">
          Bathroom remodel cost research.
        </h1>
        <p className="mt-5 text-lg text-ink-muted">
          Real numbers by scope, room size, and region — written for homeowners, not contractors. Use these to sanity-check any bid.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {GUIDES.map((g) => (
          <Link
            key={g.slug}
            to={`/guides/${g.slug}`}
            data-testid={`guides-index-card-${g.slug}`}
            className="surface-card overflow-hidden group hover:-translate-y-1 transition-transform hover:shadow-lg"
          >
            {g.image && (
              <div className="aspect-[16/10] overflow-hidden bg-bg-alt">
                <img
                  src={g.image}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.parentElement.style.display = "none";
                  }}
                />
              </div>
            )}
            <div className="p-6">
              <p className="font-mono text-xs text-ink-faint mb-3">
                {`$${(g.range.low / 1000).toFixed(1)}k – $${(g.range.high / 1000).toFixed(0)}k`}
              </p>
              <h3 className="font-display text-xl leading-snug group-hover:text-brand transition-colors">
                {g.title}
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed mt-3 line-clamp-2">
                {g.subtitle}
              </p>
              <p className="mt-4 text-sm font-medium text-brand inline-flex items-center gap-1.5">
                Read guide <ArrowRight size={14} />
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
